// Who categorized a transaction (#2162). One home for the app, the edge
// functions and the MCP server: plain JS, like bankDescription.js.
//
// transactions.category_source says WHY a row holds its category. NULL means
// unknown (every row from before the column). It is written with the
// category, never on its own, and read through these helpers.

/** The closed set the table's CHECK constraint holds. */
export const CATEGORY_SOURCES = Object.freeze(['rule', 'ai', 'bank', 'import', 'user', 'sample']);

/** @returns {boolean} */
export function isCategorySource(value) {
  return typeof value === 'string' && CATEGORY_SOURCES.includes(value);
}

/**
 * A source, or null when the value isn't one. The app never writes an
 * unknown string: the constraint would refuse the whole row.
 */
export function normalizeCategorySource(value) {
  return isCategorySource(value) ? value : null;
}

/**
 * The source a freshly categorized row carries: 'rule' when a user rule
 * decided it, else the fallback (the bank's guess, a file import's mapping, ...).
 * @param {{ rule?: any } | any | null | undefined} matched  a rule (or `{ rule }`) or nothing
 * @param {string} fallback
 */
export function sourceForMatch(matched, fallback) {
  const rule = matched && typeof matched === 'object' && 'rule' in matched ? matched.rule : matched;
  return rule ? 'rule' : normalizeCategorySource(fallback);
}

/**
 * The source an edit gives a row. A category change is the person's choice
 * ('user') unless the caller says otherwise ('ai' for an accepted suggestion,
 * 'rule' for apply-to-history).
 */
export function sourceForEdit(changes, explicit) {
  if (!changes || changes.categoryId === undefined) return null;
  return normalizeCategorySource(explicit) ?? 'user';
}

/**
 * True when a read or write failed only because this database has no
 * category_source column yet (the app can deploy before the migration,
 * CLAUDE.md old-database tolerance). PostgREST answers PGRST204 for an
 * unknown column in a write, Postgres 42703 for one in a select.
 */
export function isMissingCategorySourceColumn(error) {
  if (!error || (error.code !== 'PGRST204' && error.code !== '42703')) return false;
  return /category_source/.test(String(error.message || ''));
}

/** The same row(s) without the column: the retry after isMissingCategorySourceColumn. */
export function withoutCategorySource(rows) {
  const strip = (row) => {
    if (!row || typeof row !== 'object') return row;
    const { category_source: _dropped, ...rest } = row;
    return rest;
  };
  return Array.isArray(rows) ? rows.map(strip) : strip(rows);
}

/** i18n key suffix per source (`ledger.categorySource.<key>`). */
export const CATEGORY_SOURCE_LABEL_KEYS = Object.freeze({
  rule: 'rule', ai: 'ai', bank: 'bank', import: 'import', user: 'user', sample: 'sample',
});
